import React, { Suspense } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';

const PlantillaNewPanel = React.lazy(() => import('./PlantillaNewPanel'));

/**
 * @fileoverview Componente que muestra la lista de dashboards favoritos del usuario.
 * @module FavouritesDashboard
 */

/**
 * Componente FavouritesDashboard.
 *
 * Muestra una galería de los dashboards marcados como favoritos por el usuario.
 * Permite seleccionar uno para cargarlo como el layout activo en el panel principal.
 *
 * Utiliza `Suspense` y `React.lazy` para cargar los componentes de vista previa (`PlantillaNewPanel`)
 * bajo demanda.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} props.dashboardsFavoritos - Lista de objetos de configuración de los dashboards favoritos.
 *
 * @returns {React.ReactElement} Tarjeta contenedora con la rejilla de favoritos.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function FavouritesDashboard({ dashboardsFavoritos }) {
  const { t } = useTranslation();
  const dispatch = useDynamicViewDispatch();

  /**
   * Carga el dashboard seleccionado en el estado global.
   *
   * Actualiza:
   * 1. El layout activo (`activeLayout`).
   * 2. El objetivo global y si aplica a todos (`globalObjective`).
   * 3. El ID del layout activo (`activeLayoutId`).
   *
   * @param {Object} layout - Objeto de configuración del dashboard seleccionado.
   */
  function updateSlotParam(layout) {
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT,
      payload: layout.estructura_panel_dinamico,
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_GLOBAL_OBJECTIVE,
      payload: {
        objetivoId: layout.objetivo_id,
        applyToAll: layout.apply_to_all_objetivo_id,
      },
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT_ID,
      payload: layout.panel_dinamico_usuario_id,
    });
  }

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h3" gutterBottom>
            {t('dynamicPanel.favouritesDashboard.title')}
          </Typography>
        }
        subheader={
          <Typography variant="subtitle1" gutterBottom>
            {t('dynamicPanel.favouritesDashboard.subtitle')}
          </Typography>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          {dashboardsFavoritos.map((panel) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={3}
              xl={3}
              key={panel.panel_dinamico_usuario_id}
            >
              <Suspense fallback={<CircularProgress size={24} />}>
                <PlantillaNewPanel
                  estructura={panel.estructura_panel_dinamico}
                  onSelect={() => updateSlotParam(panel)}
                />
              </Suspense>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}
